import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import {
  Add,
  Delete,
  Edit,
  Person,
  Refresh,
  VerifiedUser,
} from '@mui/icons-material'
import { userService } from '../services/api'
import { CreateUserPayload, UpdateUserPayload, UserManagementRecord } from '../types'

type Feedback = {
  severity: 'success' | 'error' | 'info'
  message: string
}

type UserFormState = {
  fullName: string
  username: string
  password: string
  role: 'Owner' | 'Technician'
  phone: string
  isActive: boolean
}

const emptyForm: UserFormState = {
  fullName: '',
  username: '',
  password: '',
  role: 'Technician',
  phone: '',
  isActive: true,
}

const unwrapData = <T,>(response: any): T => (response?.data ?? response) as T

const formatDate = (value: string) =>
  new Date(value).toLocaleString('ar-IQ', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

export default function UsersPage() {
  const [users, setUsers] = useState<UserManagementRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [feedback, setFeedback] = useState<Feedback | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<UserManagementRecord | null>(null)
  const [form, setForm] = useState<UserFormState>(emptyForm)

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const response = await userService.getAll()
      setUsers(unwrapData<UserManagementRecord[]>(response) ?? [])
    } catch (error: any) {
      setFeedback({ severity: 'error', message: error?.response?.data?.message || 'فشل في تحميل المستخدمين' })
      setUsers([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  const filteredUsers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase()
    if (!query) return users

    return users.filter((user) =>
      [user.fullName, user.username, user.role, user.phone || '']
        .join(' ')
        .toLowerCase()
        .includes(query)
    )
  }, [users, searchTerm])

  const openCreateDialog = () => {
    setEditingUser(null)
    setForm(emptyForm)
    setDialogOpen(true)
  }

  const openEditDialog = (user: UserManagementRecord) => {
    setEditingUser(user)
    setForm({
      fullName: user.fullName,
      username: user.username,
      password: '',
      role: user.role,
      phone: user.phone || '',
      isActive: user.isActive,
    })
    setDialogOpen(true)
  }

  const submitUser = async () => {
    if (!form.fullName.trim() || !form.username.trim()) {
      setFeedback({ severity: 'error', message: 'الاسم الكامل واسم المستخدم مطلوبان' })
      return
    }

    try {
      setSaving(true)

      if (editingUser) {
        const payload: UpdateUserPayload = {
          fullName: form.fullName.trim(),
          phone: form.phone.trim() || undefined,
          isActive: form.isActive,
        }

        await userService.update(editingUser.id, payload)
        setFeedback({ severity: 'success', message: 'تم تحديث المستخدم بنجاح' })
      } else {
        if (!form.password.trim()) {
          setFeedback({ severity: 'error', message: 'كلمة المرور مطلوبة عند إنشاء المستخدم' })
          return
        }

        const payload: CreateUserPayload = {
          fullName: form.fullName.trim(),
          username: form.username.trim(),
          password: form.password.trim(),
          role: form.role,
          phone: form.phone.trim() || undefined,
          isActive: form.isActive,
        }

        await userService.create(payload)
        setFeedback({ severity: 'success', message: 'تم إنشاء المستخدم بنجاح' })
      }

      setDialogOpen(false)
      setForm(emptyForm)
      await fetchUsers()
    } catch (error: any) {
      setFeedback({ severity: 'error', message: error?.response?.data?.message || 'تعذر حفظ المستخدم' })
    } finally {
      setSaving(false)
    }
  }

  const deleteUser = async (user: UserManagementRecord) => {
    if (!window.confirm(`هل تريد حذف المستخدم ${user.fullName}؟`)) return

    try {
      await userService.delete(user.id)
      setFeedback({ severity: 'success', message: 'تم حذف المستخدم نهائياً' })
      await fetchUsers()
    } catch (error: any) {
      setFeedback({ severity: 'error', message: error?.response?.data?.message || 'تعذر حذف المستخدم' })
    }
  }

  const toggleStatus = async (user: UserManagementRecord) => {
    try {
      await userService.update(user.id, {
        fullName: user.fullName,
        phone: user.phone || undefined,
        isActive: !user.isActive,
      })
      setFeedback({
        severity: 'success',
        message: user.isActive ? 'تم تعطيل المستخدم' : 'تم تفعيل المستخدم',
      })
      await fetchUsers()
    } catch (error: any) {
      setFeedback({ severity: 'error', message: error?.response?.data?.message || 'تعذر تحديث حالة المستخدم' })
    }
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
            إدارة المستخدمين
          </Typography>
          <Typography color="text.secondary">
            هذه الصفحة مرتبطة الآن بقاعدة البيانات مباشرة، لذلك الحذف والتفعيل يبقيان ثابتين بعد التحديث.
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchUsers} disabled={loading}>
            تحديث
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={openCreateDialog}>
            إضافة مستخدم
          </Button>
        </Stack>
      </Stack>

      {feedback && (
        <Alert severity={feedback.severity} onClose={() => setFeedback(null)} sx={{ mb: 3, borderRadius: 2 }}>
          {feedback.message}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3 }}><CardContent><Typography color="text.secondary">إجمالي المستخدمين</Typography><Typography variant="h4" sx={{ fontWeight: 800 }}>{users.length}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3 }}><CardContent><Typography color="text.secondary">النشطون</Typography><Typography variant="h4" sx={{ fontWeight: 800 }}>{users.filter((user) => user.isActive).length}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3 }}><CardContent><Typography color="text.secondary">الفنيون</Typography><Typography variant="h4" sx={{ fontWeight: 800 }}>{users.filter((user) => user.role === 'Technician').length}</Typography></CardContent></Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 3 }}>
        <TextField fullWidth label="بحث بالاسم أو اسم المستخدم أو الدور" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} />
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <Grid container spacing={3}>
          {filteredUsers.map((user) => (
            <Grid item xs={12} md={6} lg={4} key={user.id}>
              <Card sx={{ height: '100%', borderRadius: 4, boxShadow: '0 14px 30px rgba(15,23,42,0.08)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48, borderRadius: 3, bgcolor: 'primary.50', color: 'primary.main' }}>
                        {user.role === 'Owner' ? <VerifiedUser /> : <Person />}
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>{user.fullName}</Typography>
                        <Typography variant="body2" color="text.secondary">@{user.username}</Typography>
                      </Box>
                    </Stack>
                    <Chip label={user.role === 'Owner' ? 'مالك' : 'فني'} color={user.role === 'Owner' ? 'secondary' : 'primary'} size="small" />
                  </Stack>

                  <Stack spacing={1} sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">الهاتف: {user.phone || 'غير مضاف'}</Typography>
                    <Typography variant="body2" color="text.secondary">تاريخ الإنشاء: {formatDate(user.createdAt)}</Typography>
                  </Stack>

                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {user.isActive ? 'الحساب نشط' : 'الحساب معطل'}
                    </Typography>
                    <Switch checked={user.isActive} onChange={() => toggleStatus(user)} />
                  </Stack>

                  <Stack direction="row" spacing={1}>
                    <Button fullWidth variant="contained" startIcon={<Edit />} onClick={() => openEditDialog(user)}>
                      تعديل
                    </Button>
                    <IconButton color="error" onClick={() => deleteUser(user)}>
                      <Delete />
                    </IconButton>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingUser ? 'تعديل المستخدم' : 'إضافة مستخدم جديد'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label="الاسم الكامل" value={form.fullName} onChange={(event) => setForm((prev) => ({ ...prev, fullName: event.target.value }))} fullWidth required />
            <TextField label="اسم المستخدم" value={form.username} onChange={(event) => setForm((prev) => ({ ...prev, username: event.target.value }))} fullWidth required disabled={!!editingUser} />
            {!editingUser && (
              <TextField label="كلمة المرور" type="password" value={form.password} onChange={(event) => setForm((prev) => ({ ...prev, password: event.target.value }))} fullWidth required />
            )}
            <TextField select label="الدور" value={form.role} onChange={(event) => setForm((prev) => ({ ...prev, role: event.target.value as 'Owner' | 'Technician' }))} fullWidth disabled={!!editingUser}>
              <MenuItem value="Owner">Owner</MenuItem>
              <MenuItem value="Technician">Technician</MenuItem>
            </TextField>
            <TextField label="رقم الهاتف" value={form.phone} onChange={(event) => setForm((prev) => ({ ...prev, phone: event.target.value }))} fullWidth />
            <TextField select label="الحالة" value={form.isActive ? 'active' : 'inactive'} onChange={(event) => setForm((prev) => ({ ...prev, isActive: event.target.value === 'active' }))} fullWidth>
              <MenuItem value="active">نشط</MenuItem>
              <MenuItem value="inactive">معطل</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDialogOpen(false)}>إلغاء</Button>
          <Button variant="contained" onClick={submitUser} disabled={saving}>{saving ? 'جاري الحفظ...' : editingUser ? 'حفظ التعديل' : 'إنشاء المستخدم'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
